/**
 * Date presets of the quick filters and the day ranges behind them (addendum A.4.2, A.5.2, ED-65).
 * All days are Amsterdam calendar days as `YYYY-MM-DD`; "today" is passed in (`todayIso()`), so
 * the ranges shown are the ones the server computes. Display only: the server owns the filtering.
 */
import type { Condition } from './types'

/** A span of days, both ends included. */
export interface DayRange {
  from: string
  to: string
}

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/
const MAX_DAYS = 3650

/** Whether `value` is a calendar day that exists, as YYYY-MM-DD (from year 1, like the server). */
export function isRealDay(value: unknown): value is string {
  const match = typeof value === 'string' ? ISO_DAY.exec(value) : null
  if (!match) return false
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])]
  if (year < 1) return false
  const date = new Date(0)
  date.setUTCFullYear(year, month - 1, day)
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
}

/** Whether `value` is a number of days the server accepts for "in the last n days": a whole 1–3650. */
export function isDayCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= MAX_DAYS
}

/** A day at noon UTC: no clock change or zone can move it to another calendar day. */
function noon(iso: string): Date {
  return new Date(`${iso}T12:00:00Z`)
}

/** A day plus whole calendar days (negative goes back), as YYYY-MM-DD; no clock change can shift it. */
export function addDays(iso: string, days: number): string {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10)
}

/** The Monday of the week of a day. */
function weekStart(iso: string): string {
  return addDays(iso, -((noon(iso).getUTCDay() + 6) % 7))
}

const firstOfMonth = (year: number, month: number): string => new Date(Date.UTC(year, month, 1)).toISOString().slice(0, 10)

/** The whole calendar period of `this_week` (Monday to Sunday), `this_month`, `this_quarter` and `this_year`. */
export function periodRange(operator: 'this_week' | 'this_month' | 'this_quarter' | 'this_year', today: string): DayRange {
  const [year, month] = today.split('-').map(Number)
  if (operator === 'this_week') {
    const monday = weekStart(today)
    return { from: monday, to: addDays(monday, 6) }
  }
  if (operator === 'this_month') return { from: firstOfMonth(year, month - 1), to: addDays(firstOfMonth(year, month), -1) }
  if (operator === 'this_quarter') {
    const first = month - 1 - ((month - 1) % 3)
    return { from: firstOfMonth(year, first), to: addDays(firstOfMonth(year, first + 3), -1) }
  }
  return { from: `${year}-01-01`, to: `${year}-12-31` }
}

/**
 * The range a past-oriented condition covers, as shown next to it (Laatste activiteit, Laatste
 * contact): `in_last_days n` is n calendar days up to and including today; "this week" is shown
 * up to today, as nothing has happened later. The `not_*` operators have an end only: `before`
 * is the first day that does NOT count (they also match relations that have none).
 */
export function pastRange(condition: Condition, today: string): DayRange | { before: string } | null {
  const { operator, value } = condition
  switch (operator) {
    case 'in_last_days':
      return isDayCount(value) ? { from: addDays(today, 1 - value), to: today } : null
    case 'this_week':
      return { from: weekStart(today), to: today }
    case 'between':
      return Array.isArray(value) && value.length === 2 && isRealDay(value[0]) && isRealDay(value[1])
        ? { from: value[0], to: value[1] }
        : null
    case 'not_in_last_days':
      return isDayCount(value) ? { before: addDays(today, 1 - value) } : null
    case 'not_since':
      return isRealDay(value) ? { before: value } : null
    case 'more_than_days_ago':
      return isDayCount(value) ? { before: addDays(today, -value) } : null
    default:
      return null
  }
}

function dayFormat(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat('nl-NL', { timeZone: 'UTC', ...options })
}

const WEEKDAY_DAY = dayFormat({ weekday: 'short', day: 'numeric', month: 'short' })
const WEEKDAY_DAY_YEAR = dayFormat({ weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
const DAY_MONTH = dayFormat({ day: 'numeric', month: 'short' })
const DAY_MONTH_YEAR = dayFormat({ day: 'numeric', month: 'short', year: 'numeric' })
const DAY_ONLY = dayFormat({ day: 'numeric' })
const LONG_DAY_MONTH = dayFormat({ day: 'numeric', month: 'long' })
const LONG_DAY_MONTH_YEAR = dayFormat({ day: 'numeric', month: 'long', year: 'numeric' })
const LONG_WEEKDAY = dayFormat({ weekday: 'long', day: 'numeric', month: 'long' })

const sameYear = (a: string, b: string): boolean => a.slice(0, 4) === b.slice(0, 4)
const sameMonth = (a: string, b: string): boolean => a.slice(0, 7) === b.slice(0, 7)

/**
 * A day in a format. Text that is no day (a link with a made-up date) comes back as it is: a chip
 * must not crash the page, and `Intl` would throw on it or roll "30 feb" over to March.
 */
function show(format: Intl.DateTimeFormat, iso: string): string {
  return isRealDay(iso) ? format.format(noon(iso)) : iso
}

/** "vr 16 okt", with the year when it is not this year. */
export function formatDay(iso: string, today: string): string {
  return show(sameYear(iso, today) ? WEEKDAY_DAY : WEEKDAY_DAY_YEAR, iso)
}

/** "16 okt", with the year when it is not this year. */
export function formatShortDate(iso: string, today: string): string {
  return show(sameYear(iso, today) ? DAY_MONTH : DAY_MONTH_YEAR, iso)
}

/** "3–16 okt", "17 sep – 16 okt", one day as "vr 16 okt"; both ends carry their year across years. */
export function formatRange({ from, to }: DayRange, today: string): string {
  if (from === to) return formatDay(from, today)
  if (!sameYear(from, to)) return `${show(DAY_MONTH_YEAR, from)} – ${show(DAY_MONTH_YEAR, to)}`
  if (sameMonth(from, to)) return `${show(DAY_ONLY, from)}–${formatShortDate(to, today)}`
  return `${formatShortDate(from, today)} – ${formatShortDate(to, today)}`
}

/** The range as a screen reader should say it: "3 tot en met 16 oktober", one day as "vrijdag 16 oktober". */
export function formatRangeSpoken({ from, to }: DayRange): string {
  if (from === to) return show(LONG_WEEKDAY, from)
  if (!sameYear(from, to)) return `${show(LONG_DAY_MONTH_YEAR, from)} tot en met ${show(LONG_DAY_MONTH_YEAR, to)}`
  if (sameMonth(from, to)) return `${show(DAY_ONLY, from)} tot en met ${show(LONG_DAY_MONTH, to)}`
  return `${show(LONG_DAY_MONTH, from)} tot en met ${show(LONG_DAY_MONTH, to)}`
}

/** One choice of a quick filter: its menu label, the short text for the button and the condition it writes. */
export interface DatePreset {
  key: string
  label: string
  short: string
  condition: { operator: string; value?: number }
}

export const CHANGED_PRESETS: DatePreset[] = [
  { key: 'vandaag', label: 'Vandaag', short: 'vandaag', condition: { operator: 'in_last_days', value: 1 } },
  { key: 'week', label: 'Deze week', short: 'deze week', condition: { operator: 'this_week' } },
  { key: '2w', label: 'Afgelopen 2 weken', short: '2 weken', condition: { operator: 'in_last_days', value: 14 } },
  { key: '30d', label: 'Afgelopen 30 dagen', short: '30 dagen', condition: { operator: 'in_last_days', value: 30 } },
]
export const RECENT_CONTACT_PRESETS: DatePreset[] = CHANGED_PRESETS.slice(2)
export const NO_CONTACT_PRESETS: DatePreset[] = [30, 60, 90].map((days) => ({
  key: `stil-${days}`,
  label: `${days} dagen`,
  short: `geen contact ${days} dagen`,
  condition: { operator: 'not_in_last_days', value: days },
}))

/** The preset a condition is, by operator and value; `null` for a custom one. */
export function matchPreset(presets: DatePreset[], condition: Condition): DatePreset | null {
  return (
    presets.find(
      (preset) => preset.condition.operator === condition.operator && preset.condition.value === condition.value,
    ) ?? null
  )
}
